import { THEME_OPTIONS } from "../components/properties/fonts";
import _ from "lodash";
import { parseColor } from "./color";

export function calculateFontSizeForMobile(fontSize) {
  const value = Math.round(fontSize * 0.4848);
  return Math.max(13, value);
}

export const cleanFontSizeFromElement = (editor, node) => {
  const dom = editor.dom;
  processChildElements(node,
    (node) => hasStyle(dom, node, 'fontSize'),
    (node) => applyStyle(dom, node, 'fontSize', '')
  );
};

const processChildElements = (node, filter, process) => {
  Array.from(node.childNodes).forEach(child => {
    if (child) {
      if (filter(child)) {
        process(child);
      }
      if (child.hasChildNodes()) {
        processChildElements(child, filter, process);
      }
    }
  });
};

const hasStyle = (dom, node, name) => !!(node && dom.getStyle(node, name));

const applyStyle = (dom, node, name, value) => {
  dom.setStyle(node, name, value);

  if (node.getAttribute('style') === '') {
    node.removeAttribute('style');
  }

  unwrapEmptySpan(dom, node);
};

const unwrapEmptySpan = (dom, node) => {
  if (node.nodeName === 'SPAN' && dom.getAttribs(node).length === 0) {
    dom.remove(node, true);
  }
};

//Get Actions for text widget
export function getActionsFromHTML(content) {
  const node = document.createElement('div');
  node.innerHTML = content;

  const anchors = node.querySelectorAll('a[data-action]');
  return Array.from(anchors).map(element => {
    return element.dataset.action;
  })
}

export function isAllContentSelected(textEditor) {
  if(!textEditor){
    return false;
  }

  var selectedNode = textEditor.selection.getNode();
  var selectedText = null;
  if(selectedNode){
    selectedText = selectedNode.textContent;
  }

  return textEditor.bodyElement.textContent === selectedText;
}

export function generateNodeFromSelection(textEditor){
  var startContainer = textEditor.selection.getStart();

  const allContentSelected = isAllContentSelected(textEditor);
  if(allContentSelected){
    startContainer = selectFirstNodeWithText(startContainer);
  }

  const span = generateNodeFromSelectedNode(textEditor.dom, startContainer);
  return span;
}

//Get Styles for node
export function generateNodeFromSelectedNode(dom, node){
  if(!node){
    return null;
  }

  const span = dom.create('span');

  const fontSize = dom.getStyle(node, 'fontSize', true);
  const fontFamily = dom.getStyle(node, 'fontFamily', true);
  const color = dom.getStyle(node, 'color', true);
  const background = dom.getStyle(node, 'background-color', true);

  const fontWeight = dom.getStyle(node, 'font-weight', true);
  const italics = dom.getStyle(node, 'font-style', true);
  const underline = dom.getStyle(node, 'text-decoration', true);

  if(!!fontSize){
    dom.setStyle(span, 'font-size', fontSize);
  }

  if(!!fontFamily){
    dom.setStyle(span, 'font-family', fontFamily);
  }

  if(!!fontWeight){
    dom.setStyle(span, 'font-weight', fontWeight);
  }

  if(!!color){
    dom.setStyle(span, 'color', color);
  }

  if(!!background){
    dom.setStyle(span, 'background-color', background);
  }

  if(!!italics){
    dom.setStyle(span, 'font-style', italics);
  }

  if(!!underline){
    dom.setStyle(span, 'text-decoration', underline);
  }

  return span;
}

function selectFirstNodeWithText(root) {
  if(!root){
    return root;
  }

  if (root.nodeType === document.TEXT_NODE) {
    var text = root.nodeValue;
    return root.parentElement;
  } else if (root.nodeType === document.ELEMENT_NODE) {
    for (var i = 0; i < root.childNodes.length; i++) {
      const child = root.childNodes[i];
      const result = selectFirstNodeWithText(child)
      if (result) {
        return result;
      }
    }
  }

  return null;
};

export function handleEmptyContentInEditor(textEditor, target){
  var startContainer = selectFirstNodeWithText(target);
  var span = generateNodeFromSelectedNode(textEditor.dom, startContainer);

  if(!!span){
    span = textEditor.dom.create('span');
  }
  
  var format ='p';
  const rootBlock = getRootBlockFromSelection(textEditor);
  if(rootBlock){
    format = rootBlock.nodeName.toLowerCase();
  }

  var formatElement = textEditor.dom.create(format);
  
  if(span){
    span.innerHTML = '&#8203;';  
    formatElement.appendChild(span);
  }
  
  return formatElement.outerHTML;
}

export function getRootBlockFromSelection(textEditor){
  const node = textEditor.selection.getNode();
  const root = getRootBlockFromNode(node);
  return root;
}

export function getRootBlockFromNode(node){
  if(!node){
    return node;
  }

  if(isRootBlock(node)){
    return node;
  }

  const parentBlock = findRootBlockInAncestors(node.parentElement);
  if(parentBlock){
    return parentBlock;
  }

  return findRootBlockFromChildren(node);
}

function findRootBlockInAncestors(node){
  if(!node){
    return null;
  }

  if(isRootBlock(node)){
    return node;
  }

  return findRootBlockInAncestors(node.parentElement);
}

function findRootBlockFromChildren(node){
  if(!node){
    return null;
  }

  var childRoot = null;
  for(var i = 0; i < node.childNodes.length; i ++){
    const child = node.childNodes[0];

    if(isRootBlock(child)){
      childRoot = child;
      break;
    }

    const rootBlock = findRootBlockFromChildren(child);
    if(rootBlock){
      childRoot = rootBlock;
      break;
    }
  }

  return childRoot;
}

function isRootBlock(node){
  if(!node){
    return false;
  }

  const nodeName = node.nodeName.toLowerCase();
  return THEME_OPTIONS.find(theme => {
    return theme.value === nodeName;
  });
}

/**
 * Generate theme from the node
 * 
*/
export function getThemeFromNode(textEditor, node) {
  const dom = textEditor.dom;
  const selectedNode = selectFirstNodeWithText(node);

  const fontSize = dom.getStyle(selectedNode, 'fontSize', true);
  const fontFamily = dom.getStyle(selectedNode, 'fontFamily', true);
  const color = dom.getStyle(selectedNode, 'color', true);

  const lineHeight = dom.getStyle(selectedNode, 'line-height', true);
  var lineSpacing = parseFloat(lineHeight) / parseFloat(fontSize);
  lineSpacing = lineSpacing.toFixed(1);

  var letterSpacing = dom.getStyle(selectedNode, 'letter-spacing', true);
  if(letterSpacing === 'normal'){
    letterSpacing = 0;
  }else {
    letterSpacing = parseFloat(letterSpacing) / parseFloat(fontSize);
    letterSpacing = letterSpacing.toFixed(1);
  }

  var fontFormat = 'p';
  const rootBlock = getRootBlockFromSelection(textEditor);
  if (rootBlock) {
    fontFormat = rootBlock.nodeName.toLowerCase();
  }

  const fontWeight = dom.getStyle(selectedNode, 'font-weight', true);
  const fontStyle = dom.getStyle(selectedNode, 'font-style', true);

  var fontStyles = {
    style: {
      bold: fontWeight >= 600,
      italic: fontStyle === 'italic',
      underline: false
    },
    fontSize: parseFloat(fontSize).toString(),
    lineSpacing: lineSpacing.toString(),
    letterSpacing: parseFloat(letterSpacing).toString(),
    fontFamily: fontFamily,
    color: parseColor(color)
  }

  return {
    type: fontFormat,
    font: fontStyles
  };
}